import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { fetchEmojisByTheme, searchEmojis, groupEmojisByCategory } from '../services/emojiService';

const GridContainer = styled.div`
  padding: 1rem;
  max-height: 70vh;
  overflow-y: auto;
`;

const SectionTitle = styled.h2`
  margin-top: 0;
  margin-bottom: 1rem;
  color: #202124;
  font-size: 1.5rem;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
`;

const CategoryTitle = styled.h3`
  margin-top: 2rem;
  margin-bottom: 1rem;
  color: #5f6368;
  font-size: 1.2rem;
  font-weight: 500;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
  font-family: 'Poppins', sans-serif;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(48px, 40px));
  gap: 0.5rem;
`;

const EmojiCard = styled.div`
  padding: 0.5rem;
  text-align: center;
  cursor: pointer;
  transition: transform 0.2s;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    transform: scale(1.1);
    background-color: #f5f5f5;
  }
  
  ${props => props.selected && `
    background-color: #e8f0fe;
    box-shadow: 0 0 0 2px #4285F4;
  `}
`;

const EmojiSprite = styled.div`
  width: 32px;
  height: 32px;
  background-image: url(${props => props.spriteUrl});
  background-size: 6200% 6200%;
  background-position: ${props => props.posX}% ${props => props.posY}%;
  background-repeat: no-repeat;
  margin: 0 auto;
`;

const ThemeInfo = styled.div`
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #5f6368;
  font-family: 'Inter', sans-serif;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 2rem;
  color: #5f6368;
  font-family: 'Inter', sans-serif;
  font-size: 1.1rem;
`;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 2rem;
  color: #5f6368;
  font-family: 'Inter', sans-serif;
  font-size: 1.1rem;
`;

function EmojiGrid({ searchTerm, theme, onEmojiClick, selectedEmoji }) {
  const [emojis, setEmojis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categorizedEmojis, setCategorizedEmojis] = useState({});

  useEffect(() => {
    const loadEmojis = async () => {
      try {
        setLoading(true);
        const data = await fetchEmojisByTheme(theme);
        setEmojis(data);
        
        // Group emojis by category
        const grouped = groupEmojisByCategory(data);
        setCategorizedEmojis(grouped);
        
        setError(null);
      } catch (err) {
        setError('Failed to load emojis. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadEmojis();
  }, [theme]);

  // Filter by search term
  const filteredEmojis = searchEmojis(emojis, searchTerm);

  // Group filtered emojis by category if there's a search term
  const filteredCategorizedEmojis = searchTerm 
    ? groupEmojisByCategory(filteredEmojis)
    : categorizedEmojis;

  const getThemeName = () => {
    switch(theme) {
      case 'apple': return 'Apple';
      case 'google': return 'Google';
      case 'twitter': return 'Twitter';
      case 'facebook': return 'Facebook';
      default: return 'Apple';
    }
  };

  if (loading) {
    return <LoadingIndicator>Loading emojis...</LoadingIndicator>;
  }

  if (error) {
    return <NoResults>{error}</NoResults>;
  }

  // If no results after filtering
  if (filteredEmojis.length === 0 && searchTerm) {
    return <NoResults>No emojis found matching "{searchTerm}"</NoResults>;
  }

  return (
    <GridContainer>
      {searchTerm ? (
        <SectionTitle>Search Results for "{searchTerm}"</SectionTitle>
      ) : (
        <SectionTitle>All Emojis</SectionTitle>
      )}
      <ThemeInfo>Showing {getThemeName()} emoji style</ThemeInfo>
      
      {Object.entries(filteredCategorizedEmojis).map(([category, categoryEmojis]) => (
        <div key={category}>
          <CategoryTitle>{category}</CategoryTitle>
          <Grid>
            {categoryEmojis.map(emoji => (
              <EmojiCard 
                key={emoji.id} 
                onClick={() => onEmojiClick(emoji)}
                selected={selectedEmoji && selectedEmoji.id === emoji.id}
              >
                <EmojiSprite 
                  spriteUrl={emoji.spriteUrl}
                  posX={emoji.posX}
                  posY={emoji.posY}
                />
              </EmojiCard>
            ))}
          </Grid>
        </div>
      ))}
    </GridContainer>
  );
}

export default EmojiGrid; 